import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Paper,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    LocationOn as LocationIcon,
    Work as WorkIcon,
    AttachMoney as MoneyIcon,
    Check as CheckIcon,
    Upload as UploadIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { jobVacancyService } from '../services/jobVacancyService';
import { JobVacancy, CreateApplicationData, SENIORITY_LABELS, WORK_MODEL_LABELS } from '../types/jobVacancy';

export const VacancyDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [vacancy, setVacancy] = useState<JobVacancy | null>(null);
    const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [applicationData, setApplicationData] = useState<CreateApplicationData>({
        candidate_name: '',
        candidate_email: '',
        resume: null as unknown as File,
        linkedin_url: '',
        phone: '',
        salary_expectation: undefined,
        availability_date: ''
    });

    useEffect(() => {
        loadVacancy();
    }, [id]);

    const loadVacancy = async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await jobVacancyService.getPublicVacancy(Number(id));
            setVacancy(data);
        } catch (error) {
            enqueueSnackbar('Erro ao carregar vaga', { variant: 'error' });
            navigate('/vagas/publicas');
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setApplicationData((prev: CreateApplicationData) => ({
                ...prev,
                resume: event.target.files![0]
            }));
        }
    };

    const handleInputChange = (field: keyof CreateApplicationData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setApplicationData((prev: CreateApplicationData) => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleApplicationSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!vacancy) return;
        if (!applicationData.resume) {
            enqueueSnackbar('Anexe o currículo (PDF, DOC ou DOCX)', { variant: 'warning' });
            return;
        }

        try {
            setSubmitting(true);
            await jobVacancyService.applyToPublicVacancy(vacancy.id, applicationData);
            enqueueSnackbar('Candidatura enviada com sucesso', { variant: 'success' });
            setApplicationDialogOpen(false);
            setApplicationData({
                candidate_name: '',
                candidate_email: '',
                resume: null as unknown as File,
                linkedin_url: '',
                phone: '',
                salary_expectation: undefined,
                availability_date: ''
            });
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.error || 'Erro ao enviar candidatura', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!vacancy) {
        return null;
    }

    return (
        <Box sx={{ 
            minHeight: '100vh',
            bgcolor: '#f5f5f5',
            pt: 4,
            pb: 8
        }}>
            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/vagas/publicas')}
                    sx={{ mb: 4 }}
                >
                    Voltar para lista de vagas
                </Button>

                <Paper sx={{ p: 4, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                {vacancy.role}
                            </Typography>
                            <Typography variant="h6" color="primary" gutterBottom>
                                {vacancy.seniority && (SENIORITY_LABELS[vacancy.seniority] || vacancy.seniority)}
                            </Typography>
                        </Box>
                    </Box>

                    <Stack spacing={3}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {vacancy.location && (
                                <Chip
                                    icon={<LocationIcon />}
                                    label={vacancy.location}
                                    variant="outlined"
                                />
                            )}
                            {vacancy.work_model && (
                                <Chip
                                    icon={<WorkIcon />}
                                    label={
                                        vacancy.work_model === 'REMOTE' ? 'Remoto' :
                                        vacancy.work_model === 'HYBRID' ? 'Híbrido' : 'Presencial'
                                    }
                                    variant="outlined"
                                />
                            )}
                            {vacancy.contract_type && (
                                <Chip
                                    icon={<MoneyIcon />}
                                    label={vacancy.contract_type}
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Descrição da vaga
                            </Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                {vacancy.description}
                            </Typography>
                        </Box>

                        {vacancy.requirements && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Requisitos
                                </Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                    {vacancy.requirements}
                                </Typography>
                            </Box>
                        )}

                        {vacancy.benefits && (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Benefícios
                                </Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                    {vacancy.benefits}
                                </Typography>
                            </Box>
                        )}

                        <Divider />

                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => setApplicationDialogOpen(true)}
                                sx={{ 
                                    minWidth: 250,
                                    py: 2,
                                    fontSize: '1.1rem'
                                }}
                            >
                                Candidatar-se
                            </Button>
                        </Box>
                    </Stack>
                </Paper>

                {/* Application Dialog */}
                <Dialog
                    open={applicationDialogOpen}
                    onClose={() => setApplicationDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Typography variant="h5" component="div" gutterBottom>
                            Candidatar-se para {vacancy.role}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {[
                                vacancy.seniority && (SENIORITY_LABELS[vacancy.seniority] || vacancy.seniority),
                                vacancy.location,
                                vacancy.work_model && (WORK_MODEL_LABELS[vacancy.work_model] || vacancy.work_model),
                            ].filter(Boolean).join(' • ')}
                        </Typography>
                    </DialogTitle>
                    <form onSubmit={handleApplicationSubmit}>
                        <DialogContent>
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    label="Nome completo"
                                    value={applicationData.candidate_name}
                                    onChange={handleInputChange('candidate_name')}
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="E-mail"
                                    type="email"
                                    value={applicationData.candidate_email}
                                    onChange={handleInputChange('candidate_email')}
                                    required
                                />
                                <TextField
                                    fullWidth
                                    label="Telefone"
                                    value={applicationData.phone}
                                    onChange={handleInputChange('phone')}
                                />
                                <TextField
                                    fullWidth
                                    label="LinkedIn URL"
                                    value={applicationData.linkedin_url}
                                    onChange={handleInputChange('linkedin_url')}
                                    placeholder="https://linkedin.com/in/seu-perfil"
                                />
                                <TextField
                                    fullWidth
                                    label="Pretensão salarial"
                                    type="number"
                                    value={applicationData.salary_expectation}
                                    onChange={handleInputChange('salary_expectation')}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">R$</InputAdornment>
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    label="Data de disponibilidade"
                                    type="date"
                                    value={applicationData.availability_date}
                                    onChange={handleInputChange('availability_date')}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                                <input
                                    accept=".pdf,.doc,.docx"
                                    style={{ display: 'none' }}
                                    id="resume-file"
                                    type="file"
                                    onChange={handleFileChange}
                                    required
                                />
                                <Box>
                                    <label htmlFor="resume-file">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            fullWidth
                                            startIcon={applicationData.resume ? <CheckIcon /> : <UploadIcon />}
                                        >
                                            {applicationData.resume ? 'Currículo selecionado' : 'Upload do Currículo'}
                                        </Button>
                                    </label>
                                    {applicationData.resume && (
                                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'success.main' }}>
                                            Arquivo selecionado: {applicationData.resume.name}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ p: 3 }}>
                            <Button onClick={() => setApplicationDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={submitting}
                                startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                            >
                                {submitting ? 'Enviando...' : 'Enviar candidatura'}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>
            </Container>
        </Box>
    );
}; 